<template>
  <Listbox
    as="div"
    v-model="computedModelValue"
    multiple
    class="w-full"
  >
    <ListboxLabel
      v-if="hasLabelContent"
      class="block text-sm font-medium text-gray-700"
    >
      <slot />
    </ListboxLabel>

    <div
      class="flex relative w-full"
      :class="{ 'mt-1': hasLabelContent }"
    >
      <ListboxButton
        class="relative w-full border border-gray-300 bg-gray-100 rounded-md shadow-sm pl-3 pr-10 text-left cursor-default sm:text-sm multi-select"
        :class="({ 'rounded-r-md': !hasAppendContent }, innerPadding)"
      >
        <span class="block truncate">
          {{ selectedText || placeholder }}
        </span>
        <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <SelectorIcon
            class="h-5 w-5 text-gray-600"
            aria-hidden="true"
          />
        </span>
      </ListboxButton>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="absolute z-10 mt-1 w-full bg-gray-100 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-gray-400 ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
        >
          <ListboxOption
            as="template"
            v-for="option in options"
            :key="option.value"
            :value="option"
            v-slot="{ active, selected }"
          >
            <li
              :class="[
                active ? 'text-gray-100 bg-blue-600' : 'text-gray-900',
                'cursor-default select-none relative py-2 pl-8 pr-4'
              ]"
            >
              <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                {{ option.label }}
              </span>
              <span
                v-if="selected"
                :class="[
                  active ? 'text-gray-100' : 'text-blue-600',
                  'absolute inset-y-0 left-0 flex items-center pl-1.5'
                ]"
              >
                <CheckIcon
                  class="h-5 w-5"
                  aria-hidden="true"
                />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>

      <slot
        v-if="hasAppendContent"
        name="append"
      ></slot>
    </div>
  </Listbox>
</template>

<script setup>
import { computed, defineProps, defineEmits, useSlots, ref } from 'vue'
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid'

const props = defineProps({
  modelValue: Array,
  options: Array,
  placeholder: {
    type: String,
    default() {
      return 'Nothing selected...'
    }
  },
  innerPadding: {
    type: String,
    default: 'py-3'
  }
})

const emit = defineEmits(['update:modelValue'])

const slots = useSlots()

const selectedText = ref('')

const computedModelValue = computed({
  get: () => {
    const valuesWithLabels = []
    selectedText.value = ''

    props.modelValue.forEach((optionValue) => {
      const option = props.options.find((option) => option.value === optionValue)
      valuesWithLabels.push(option)
      selectedText.value = selectedText.value ? `${selectedText.value}, ${option.label}` : option.label
    })

    return valuesWithLabels
  },
  set: (value) => {
    const valuesOnly = value.map((item) => item.value)
    emit('update:modelValue', valuesOnly)
  }
})

const hasLabelContent = computed(() => {
  return !!slots.default
})

const hasAppendContent = computed(() => {
  return !!slots.append
})
</script>
